import { toConsumableArray as _toConsumableArray } from '../_virtual/_rollupPluginBabelHelpers.js';
import fastMemoize from 'fast-memoize';
import { bool } from './styled-props.esm.js';
import { background } from '../styles-system/background.esm.js';
import { border } from '../styles-system/border.esm.js';
import { color } from '../styles-system/color.esm.js';
import { emSpace } from '../styles-system/em-space.esm.js';
import { flexbox } from '../styles-system/flexbox.esm.js';
import { grid } from '../styles-system/grid.esm.js';
import { layout } from '../styles-system/layout.esm.js';
import { position } from '../styles-system/position.esm.js';
import { shadow } from '../styles-system/shadow.esm.js';
import { space } from '../styles-system/space.esm.js';
import { stylable } from '../styles-system/stylable.esm.js';
import { systemProps } from '../styles-system/system-props.esm.js';
import { typography } from '../styles-system/typography.esm.js';

var props = fastMemoize(function (isSvg) {
  return [].concat(_toConsumableArray(systemProps(isSvg)), ['sx', 'variant']);
});
var paddingXProps = ['p', 'px', 'pl', 'pr', 'paddingX', 'paddingLeft', 'paddingRight', 'emPaddingX', 'emPaddingLeft', 'emPaddingRight'];
var paddingYProps = ['p', 'py', 'pt', 'pb', 'paddingY', 'paddingTop', 'paddingBottom', 'emPaddingY', 'emPaddingTop', 'emPaddingBottom'];
var marginXProps = ['m', 'mx', 'ml', 'mr', 'marginX', 'marginLeft', 'marginRight', 'emMarginX', 'emMarginLeft', 'emMarginRight'];
var marginYProps = ['m', 'my', 'mt', 'mb', 'marginY', 'marginTop', 'marginBottom', 'emMarginY', 'emMarginTop', 'emMarginBottom'];
var positionProps = _toConsumableArray(position.propNames);
var layoutPropNames = [].concat(_toConsumableArray(layout.propNames), ['gap']);
var typographyPropName = _toConsumableArray(typography.propNames);
var flexboxPropNames = [].concat(_toConsumableArray(flexbox.propNames), ['gap']);
var spacePropNames = [].concat(_toConsumableArray(space.propNames), _toConsumableArray(emSpace.propNames));
var gridPropNames = _toConsumableArray(grid.propNames);
var borderPropNames = [].concat(_toConsumableArray(border.propNames), ['boxShadow']);
var backgroundPropNames = _toConsumableArray(background.propNames);
var colorPropNames = _toConsumableArray(color.propNames);
var shadowPropNames = _toConsumableArray(shadow.propNames);
var stylablePropsNames = _toConsumableArray(stylable.propNames);
var svgPropNames = ['fill', 'stroke', 'strokeWidth', 'fillRule'];
var styleSystemPropNames = [].concat(_toConsumableArray(positionProps), _toConsumableArray(layoutPropNames), _toConsumableArray(typographyPropName), _toConsumableArray(flexboxPropNames), _toConsumableArray(spacePropNames), _toConsumableArray(borderPropNames), _toConsumableArray(backgroundPropNames), _toConsumableArray(colorPropNames), _toConsumableArray(shadowPropNames), _toConsumableArray(stylablePropsNames), _toConsumableArray(gridPropNames));
var themePropNames = ['size', 'variant'];
var _getProps = function _getProps(test) {
  return function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var next = {};
    for (var key in props) {
      if (test(key || '')) next[key] = props[key];
    }
    return next;
  };
};
var PRE = function PRE(isSvg) {
  return new RegExp("^(".concat(props(isSvg).join('|'), ")$"));
};
var TYPOGRAPHY = new RegExp("^(".concat(typographyPropName.join('|'), ")$"));
var FLEX = new RegExp("^(".concat(flexboxPropNames.join('|'), ")$"));
var PADDING = new RegExp("^(".concat(_toConsumableArray(new Set([].concat(paddingXProps, paddingYProps))).join('|'), ")$"));
var PADDING_X = new RegExp("^(".concat(paddingXProps.join('|'), ")$"));
var PADDING_Y = new RegExp("^(".concat(paddingYProps.join('|'), ")$"));
var MARGIN = new RegExp("^(".concat(_toConsumableArray(new Set([].concat(marginXProps, marginYProps))).join('|'), ")$"));
var MARGIN_X = new RegExp("^(".concat(marginXProps.join('|'), ")$"));
var MARGIN_Y = new RegExp("^(".concat(marginYProps.join('|'), ")$"));
var POSITION = new RegExp("^(".concat(positionProps.join('|'), ")$"));
var SPACE = new RegExp("^(".concat(spacePropNames.join('|'), ")$"));
var LAYOUT = new RegExp("^(".concat(layoutPropNames.join('|'), ")$"));
var GRID = new RegExp("^(".concat(gridPropNames.join('|'), ")$"));
var BORDER = new RegExp("^(".concat(borderPropNames.join('|'), ")$"));
var BACKGROUND = new RegExp("^(".concat(backgroundPropNames.join('|'), ")$"));
var COLOR = new RegExp("^(".concat(colorPropNames.join('|'), ")$"));
var ARIA = new RegExp('aria-\\w');
var DATA = new RegExp('data-\\w');
var SVG = new RegExp("^(".concat(svgPropNames.join('|'), ")$"));
var SHADOW = new RegExp("^(".concat(shadowPropNames.join('|'), ")$"));
var STYLE_SYSTEM = new RegExp("^(".concat(styleSystemPropNames.join('|'), ")$"));
var THEME_PROPS = new RegExp("^(".concat(themePropNames.join('|'), ")$"));
var THEMEABLE_PROPS = new RegExp("^(".concat(['resetCss', 'css', '__css', '__themeKey', 'type', '__inputProps', 'state', 'disableBorderEffect', 'numberOfColumnGrouping'].concat(_toConsumableArray(styleSystemPropNames), themePropNames).join('|'), ")$"));
var getSystemProps = function getSystemProps(isSvg) {
  return _getProps(function (k) {
    return PRE(isSvg).test(k);
  });
};
var getTypography = _getProps(function (k) {
  return TYPOGRAPHY.test(k);
});
var omitTypography = _getProps(function (k) {
  return !TYPOGRAPHY.test(k);
});
var getFlex = _getProps(function (k) {
  return FLEX.test(k);
});
var omitFlex = _getProps(function (k) {
  return !FLEX.test(k);
});
var getPadding = _getProps(function (k) {
  return PADDING.test(k);
});
var omitPadding = _getProps(function (k) {
  return !PADDING.test(k);
});
var getPaddingX = _getProps(function (k) {
  return PADDING_X.test(k);
});
var omitPaddingX = _getProps(function (k) {
  return !PADDING_X.test(k);
});
var getPaddingY = _getProps(function (k) {
  return PADDING_Y.test(k);
});
var omitPaddingY = _getProps(function (k) {
  return !PADDING_Y.test(k);
});
var getMargin = _getProps(function (k) {
  return MARGIN.test(k);
});
var omitMargin = _getProps(function (k) {
  return !MARGIN.test(k);
});
var getMarginX = _getProps(function (k) {
  return MARGIN_X.test(k);
});
var omitMarginX = _getProps(function (k) {
  return !MARGIN_X.test(k);
});
var getMarginY = _getProps(function (k) {
  return MARGIN_Y.test(k);
});
var omitMarginY = _getProps(function (k) {
  return !MARGIN_Y.test(k);
});
var getSpace = _getProps(function (k) {
  return SPACE.test(k);
});
var omitSpace = _getProps(function (k) {
  return !SPACE.test(k);
});
var getLayout = _getProps(function (k) {
  return LAYOUT.test(k);
});
var omitLayout = _getProps(function (k) {
  return !LAYOUT.test(k);
});
var getPosition = _getProps(function (k) {
  return POSITION.test(k);
});
var omitPosition = _getProps(function (k) {
  return !POSITION.test(k);
});
var getGrid = _getProps(function (k) {
  return GRID.test(k);
});
var omitGrid = _getProps(function (k) {
  return !GRID.test(k);
});
var getBorder = _getProps(function (k) {
  return BORDER.test(k);
});
var omitBorder = _getProps(function (k) {
  return !BORDER.test(k);
});
var getBackground = _getProps(function (k) {
  return BACKGROUND.test(k);
});
var omitBackground = _getProps(function (k) {
  return !BACKGROUND.test(k);
});
var getColor = _getProps(function (k) {
  return COLOR.test(k);
});
var omitColor = _getProps(function (k) {
  return !COLOR.test(k);
});
var getAriaPrefix = _getProps(function (k) {
  return ARIA.test(k);
});
var getDataPrefix = _getProps(function (k) {
  return DATA.test(k);
});
var getSvg = _getProps(function (k) {
  return SVG.test(k);
});
var omitSvg = _getProps(function (k) {
  return !SVG.test(k);
});
var getShadow = _getProps(function (k) {
  return SHADOW.test(k);
});
var omitShadow = _getProps(function (k) {
  return !SHADOW.test(k);
});
var getStyleSystemProps = _getProps(function (k) {
  return STYLE_SYSTEM.test(k);
});
var omitStyleSystemProp = _getProps(function (k) {
  return !STYLE_SYSTEM.test(k);
});
var omitThemeProps = _getProps(function (k) {
  return !THEME_PROPS.test(k);
});
var getThemeableProps = _getProps(function (k) {
  return THEMEABLE_PROPS.test(k);
});
var positions = bool('position', ['static', 'absolute', 'fixed', 'relative', 'sticky'], true);
function containsOmitPropSymbol(props) {
  return Object.keys(props).some(function (p) {
    return props[p] === '-';
  });
}
function propValueIsSet(props, field) {
  function test(f) {
    return f in props && (props[f] === 0 || typeof props[f] === 'string');
  }
  return Array.isArray(field) ? field.some(test) : test(field);
}
function propsEqualityCheck(name) {
  return function (prevProps, nextProps) {
    var nextPropsKeys = Object.keys(nextProps);
    var prevPropsKeys = Object.keys(prevProps);
    if (nextPropsKeys.length !== prevPropsKeys.length) {
      // eslint-disable-next-line no-console
      console.log(" ---- ".concat(name, " props are not equal ----"));
      return false;
    }
    return nextPropsKeys.every(function (k) {
      var isEqual = prevProps[k] === nextProps[k];
      if (!isEqual) {
        // eslint-disable-next-line no-console
        console.log(" ---- ".concat(name, " [").concat(k, "] not equal \r prevProps: ").concat(prevProps[k], " \r nextProps: ").concat(nextProps[k], " \r ----"));
      }
      return isEqual;
    });
  };
}

export { containsOmitPropSymbol, getAriaPrefix, getBackground, getBorder, getColor, getDataPrefix, getFlex, getGrid, getLayout, getMargin, getMarginX, getMarginY, getPadding, getPaddingX, getPaddingY, getPosition, getShadow, getSpace, getStyleSystemProps, getSvg, getSystemProps, getThemeableProps, getTypography, omitBackground, omitBorder, omitColor, omitFlex, omitGrid, omitLayout, omitMargin, omitMarginX, omitMarginY, omitPadding, omitPaddingX, omitPaddingY, omitPosition, omitShadow, omitSpace, omitStyleSystemProp, omitSvg, omitThemeProps, omitTypography, positions, propValueIsSet, propsEqualityCheck, spacePropNames };
//# sourceMappingURL=props-utils.esm.js.map
